import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import LoadingProgressBar from 'components/Shared/LoadingProgressBar'
import 'react-circular-progressbar/dist/styles.css'
import { getStageOrder, TestCampaignStage, TestCampaignStatus } from './testCampaignUtils'

const progressBarStyles = {
  trail: { stroke: '#8FB2CB' },
  path: { stroke: '#286D9B', transition: 'stroke-dashoffset 2s ease 0s', strokeLinecap: 'butt' },
}

function Circle({ title, fill, failed = false, inProgress = false, center = true, last = false }) {
  const [percentage, setPercentage] = React.useState(80)

  React.useEffect(() => {
    if (!inProgress) return
    const timeout = setTimeout(() => setPercentage((p) => (p === 80 ? 45 : 80)), 1000)
    return () => clearTimeout(timeout)
  }, [inProgress, percentage])

  return (
    <div
      className={`package-builder-page_progress-bar_circle_wrapper ${fill ? 'fill' : ''} ${failed ? 'failed' : ''} ${inProgress ? 'in-progress' : ''}`}
    >
      {inProgress ? (
        <div className="package-builder-page_progress-bar_circle_progress">
          <LoadingProgressBar visible={true} styleObject={progressBarStyles} />
        </div>
      ) : (
        <div
          className={`package-builder-page_progress-bar_circle_inner ${fill ? 'fill' : ''} ${failed ? 'failed' : ''}`}
        />
      )}
      <strong
        className={`package-builder-page_progress-bar_circle_title ${last ? 'last' : ''} ${center ? 'center' : ''}`}
      >
        {title}
      </strong>
    </div>
  )
}

Circle.propTypes = {
  title: PropTypes.string.isRequired,
  fill: PropTypes.bool.isRequired,
  failed: PropTypes.bool,
  inProgress: PropTypes.bool,
  center: PropTypes.bool,
  last: PropTypes.bool,
}

function Line({ fill = false }) {
  return (
    <div className="package-builder-page_progress-bar_line_wrapper">
      <div className={`package-builder-page_progress-bar_line_inner ${fill ? 'fill' : ''}`} />
    </div>
  )
}

Line.propTypes = { fill: PropTypes.bool.isRequired }

export default function GoogleProgressWizard({ campaign }) {
  const { t } = useTranslation()

  const steps = [
    {
      stage: TestCampaignStage.CAMPAIGN,
      idField: 'campaign_id',
      label: t('Settings.GoogleAccess.TestCampaign.progress.campaign', 'Create Campaign'),
      order: 1,
    },
    {
      stage: TestCampaignStage.ADSET,
      idField: 'adset_id',
      label: t('Settings.GoogleAccess.TestCampaign.progress.adGroup', 'Create Ad Group'),
      order: 2,
    },
    {
      stage: TestCampaignStage.AD,
      idField: 'ad_id',
      label: t('Settings.GoogleAccess.TestCampaign.progress.ad', 'Create Ad'),
      order: 3,
    },
  ]

  const isFailed = campaign.status === TestCampaignStatus.FAILURE

  const getStepStatus = (step) => {
    if (campaign.status === TestCampaignStatus.SUCCESS) return 'completed'

    const isCompleted = getStageOrder(campaign.stage) >= step.order && !!campaign[step.idField]
    const isCurrentStage =
      (!campaign.stage && step.stage === TestCampaignStage.CAMPAIGN) ||
      campaign.stage === step.stage

    if (isCompleted) return 'completed'
    if (isCurrentStage) return isFailed ? 'failed' : 'in-progress'
    return 'pending'
  }

  return (
    <div className="package-builder-page_progress-bar_outer">
      <div className="package-builder-page_progress-bar_wrapper">
        {steps.map((step, index) => {
          const status = getStepStatus(step)
          const isLastStep = index === steps.length - 1
          const isInProgress =
            status === 'in-progress' ||
            (status === 'pending' && step.stage === TestCampaignStage.CAMPAIGN)
          const stepFailed = status === 'failed'
          const isFilled = status === 'completed' || isInProgress || stepFailed

          return (
            <React.Fragment key={step.stage}>
              <Circle
                title={step.label}
                fill={isFilled}
                failed={stepFailed}
                inProgress={isInProgress}
                center={index !== 0 && !isLastStep}
                last={isLastStep}
              />
              {!isLastStep && <Line fill={status === 'completed'} />}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

GoogleProgressWizard.propTypes = {
  campaign: PropTypes.shape({
    status: PropTypes.string.isRequired,
    stage: PropTypes.string,
    campaign_id: PropTypes.string,
    adset_id: PropTypes.string,
    ad_id: PropTypes.string,
  }).isRequired,
}
